import { Dialog, Transition } from '@headlessui/react';
import { IconX, IconInbox } from '@tabler/icons-react';
import { Fragment, useState, useEffect } from 'react';
import { http, imageBaseUrl } from '../../config/http';
import { resolveItemImageUrl } from '../../utils/itemImage';
import ImageMagnifier from '../../components/common/ImageMagnifier';
import { useMasterData } from '../../context/MasterDataContext';

export default function DetailTransaksiPembelian({
  closeModal,
  isOpen,
  detailPembelian,
}) {
  const [openImage, setOpenImage] = useState(false);
  const [previewImgSrc, setPreviewImgSrc] = useState(null);
  const [activeTab, setActiveTab] = useState('pembelian');

  const { kategoriList = [] } = useMasterData() || {};
  const [localKategori, setLocalKategori] = useState([]);

  useEffect(() => {
    if ((!kategoriList || kategoriList.length === 0) && isOpen) {
      http.get('/kategori')
        .then((res) => {
          const d = res.data?.data || res.data;
          if (Array.isArray(d)) setLocalKategori(d);
        })
        .catch(() => {});
    }
  }, [isOpen, kategoriList]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab('pembelian');
      setPreviewImgSrc(null);
    }
  }, [isOpen, detailPembelian]);

  const itemTerkait = detailPembelian?.item_terkait;
  const hasItemTerkait = Boolean(
    itemTerkait &&
    typeof itemTerkait === 'object' &&
    itemTerkait.kode_item &&
    itemTerkait.kode_item !== '-' &&
    String(itemTerkait.kode_item).trim() !== ''
  );

  const allKategori = (kategoriList && kategoriList.length > 0) ? kategoriList : localKategori;

  const getJenisAndKategori = (jenisId, existingJenis) => {
    let namaJenis =
      existingJenis?.nama_jenis ||
      existingJenis?.nama_item ||
      existingJenis?.nama_barang ||
      '';
    let namaKategori = existingJenis?.kategori?.nama_kategori || '';

    if ((!namaJenis || namaJenis === '-') && jenisId) {
      for (const kat of allKategori) {
        if (Array.isArray(kat.jenis_kategori)) {
          const found = kat.jenis_kategori.find(
            (j) => String(j.id) === String(jenisId)
          );
          if (found) {
            namaJenis = found.nama_jenis;
            if (!namaKategori) {
              namaKategori = kat.nama_kategori;
            }
            break;
          }
        }
      }
    }

    return {
      namaJenis: namaJenis || '-',
      namaKategori: namaKategori || '-',
    };
  };

  const itemTerkaitInfo = getJenisAndKategori(
    itemTerkait?.jenis_kategori_id,
    itemTerkait?.jenis_kategori || itemTerkait
  );

  const pembelianInfo = getJenisAndKategori(
    detailPembelian?.jenis_kategori_id,
    detailPembelian?.jenis_kategori || detailPembelian
  );

  function numberWithCommas(x) {
    if (x === null || x === undefined || x === '') return '0';
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function roundBerat(number) {
    if (number === null || number === undefined || number === '') return '0.000';
    const num = Number(number);
    return isNaN(num) ? '0.000' : num.toFixed(3);
  }

  const beratItem = Number(detailPembelian?.berat) || 0;
  const totalHarga = Number(detailPembelian?.total_harga) || 0;
  const harga_beli_gram =
    beratItem > 0
      ? totalHarga / beratItem
      : Number(detailPembelian?.harga_beli) || 0;

  const isBersurat = !(
    detailPembelian?.surat === 0 ||
    detailPembelian?.surat === '0' ||
    detailPembelian?.surat === false ||
    String(detailPembelian?.surat).toLowerCase() === 'tidak bersurat'
  );

  const getImageUrl = (itemObj) => {
    const target = itemObj || detailPembelian;
    const resolved = resolveItemImageUrl(target);
    if (resolved) return resolved;

    const g = target?.gambar || target?.gambar1 || target?.foto;
    if (!g) return null;
    if (Array.isArray(g) && g.length > 0) {
      const path =
        typeof g[0] === 'object' && g[0] !== null
          ? g[0].asli || g[0].thumbnail
          : g[0];
      if (!path) return null;
      return path.startsWith('http') ? path : `${imageBaseUrl}${path}`;
    }
    if (typeof g === 'string') {
      try {
        const parsed = JSON.parse(g);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const path =
            typeof parsed[0] === 'object' && parsed[0] !== null
              ? parsed[0].asli || parsed[0].thumbnail
              : parsed[0];
          if (!path) return null;
          return path.startsWith('http') ? path : `${imageBaseUrl}${path}`;
        }
      } catch {
        return g.startsWith('http') ? g : `${imageBaseUrl}${g}`;
      }
    }
    return null;
  };

  const imgSrc = getImageUrl(detailPembelian);
  const itemTerkaitImgSrc = getImageUrl(itemTerkait);

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[90]" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" />
          </Transition.Child>

          {/* Full Image Preview Lightbox */}
          {openImage && isOpen && (previewImgSrc || imgSrc || itemTerkaitImgSrc) && (
            <div
              onClick={() => setOpenImage(false)}
              className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 cursor-pointer"
            >
              <button
                type="button"
                onClick={() => setOpenImage(false)}
                className="absolute top-5 right-5 p-2 bg-slate-800/80 hover:bg-slate-700 text-white rounded-full transition shadow-lg cursor-pointer"
              >
                <IconX size={24} />
              </button>
              <img
                src={previewImgSrc || imgSrc || itemTerkaitImgSrc}
                alt="Item Preview"
                className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 sm:p-6 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg sm:max-w-xl transform overflow-hidden rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 sm:p-6 text-left align-middle shadow-2xl transition-all">
                  {/* Header */}
                  <div className="flex items-start justify-between pb-3">
                    <div>
                      <Dialog.Title
                        as="h2"
                        className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-white tracking-tight"
                      >
                        Detail Pembelian
                      </Dialog.Title>
                      <div className="flex items-center gap-2 mt-1 text-sm flex-wrap">
                        <span className="text-slate-500 dark:text-slate-400 font-medium">
                          Faktur:
                        </span>
                        <span className="font-mono font-bold text-amber-600 dark:text-amber-400 tracking-wide">
                          {detailPembelian?.kode_trx || '-'}
                        </span>
                        {Boolean(detailPembelian?.is_refund) && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-400 border border-rose-300 dark:border-rose-900/40">
                            Dibatalkan / Refund
                          </span>
                        )}
                      </div>
                      {Boolean(detailPembelian?.is_refund) && (
                        <div className="mt-2.5 p-2.5 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 text-xs text-rose-700 dark:text-rose-300">
                          <span className="font-bold block">Transaksi Telah Direfund / Dibatalkan</span>
                          {detailPembelian?.alasan_hapus && (
                            <span className="text-[11px] text-rose-600 dark:text-rose-400 block mt-0.5">
                              Alasan: {detailPembelian.alasan_hapus}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2.5">
                      <button
                        type="button"
                        onClick={closeModal}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                        title="Tutup"
                      >
                        <IconX size={22} />
                      </button>
                    </div>
                  </div>

                  {/* Tabs */}
                  <div className="flex space-x-4 border-b border-slate-200 dark:border-slate-800 mb-4 px-1">
                    <button
                      type="button"
                      className={`py-2 text-sm font-bold transition-colors border-b-2 cursor-pointer ${
                        activeTab === 'pembelian'
                          ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                          : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
                      }`}
                      onClick={() => setActiveTab('pembelian')}
                    >
                      Detail Pembelian
                    </button>
                    <button
                      type="button"
                      className={`py-2 text-sm font-bold transition-colors border-b-2 cursor-pointer ${
                        activeTab === 'item_terkait'
                          ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                          : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
                      }`}
                      onClick={() => setActiveTab('item_terkait')}
                    >
                      Item Terkait (Database)
                    </button>
                  </div>

                  {activeTab === 'pembelian' ? (
                    <>

                  {/* Card Informasi Barang */}
                  <div className="mt-3 bg-slate-50/70 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 rounded-xl overflow-hidden divide-y divide-slate-100 dark:divide-slate-700/60">
                    <div className="px-5 py-2 bg-amber-50/80 dark:bg-amber-950/20 border-b border-amber-100 dark:border-amber-900/30">
                      <h3 className="text-amber-700 dark:text-amber-400 font-bold text-sm sm:text-base tracking-wide">
                        Informasi Barang
                      </h3>
                    </div>

                    {/* Row 1: Nama Barang */}
                    <div className="px-5 py-2 flex items-center justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400 font-medium">
                        Nama Barang
                      </span>
                      <span className="text-amber-600 dark:text-amber-400 font-bold text-sm">
                        {pembelianInfo.namaJenis}
                      </span>
                    </div>

                    {/* Row 2: Berat */}
                    <div className="px-5 py-2 flex items-center justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400 font-medium">
                        Berat
                      </span>
                      <div className="text-right">
                        <span className="font-bold text-slate-800 dark:text-white">
                          {roundBerat(detailPembelian?.berat)}
                        </span>{' '}
                        <span className="text-slate-500 dark:text-slate-400 font-normal">
                          Gram
                        </span>
                      </div>
                    </div>

                    {/* Row 3: Emas */}
                    <div className="px-5 py-2 flex items-center justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400 font-medium">
                        Kadar Emas
                      </span>
                      <div className="text-right">
                        <span className="font-bold text-slate-800 dark:text-white">
                          {detailPembelian?.karat || 0}
                        </span>{' '}
                        <span className="text-slate-500 dark:text-slate-400 font-normal">
                          Karat
                        </span>
                      </div>
                    </div>

                    {/* Row 4: Modal Beli / gram */}
                    <div className="px-5 py-2 flex items-center justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400 font-medium">
                        Modal Beli / gram
                      </span>
                      <span className="font-bold text-slate-600 dark:text-slate-400">
                        Rp {numberWithCommas(Math.round(harga_beli_gram || 0))}
                      </span>
                    </div>

                    {/* Row 5: Total Harga */}
                    <div className="px-5 py-2 flex items-center justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400 font-medium">
                        Total Harga
                      </span>
                      <span className="font-bold text-amber-600 dark:text-amber-400">
                        Rp {numberWithCommas(detailPembelian?.total_harga ?? 0)}
                      </span>
                    </div>

                    {/* Row 6: Surat */}
                    <div className="px-5 py-2 flex items-center justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400 font-medium">
                        Surat
                      </span>
                      <span
                        className={`font-semibold text-sm ${
                          isBersurat
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-rose-500 dark:text-rose-400'
                        }`}
                      >
                        {isBersurat ? 'Bersurat' : 'Tidak Bersurat'}
                      </span>
                    </div>

                    {/* Row 7: Kondisi */}
                    <div className="px-5 py-2 flex items-center justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400 font-medium">
                        Kondisi
                      </span>
                      <span className="text-slate-700 dark:text-slate-200 font-semibold text-sm">
                        {detailPembelian?.kondisi_barang || '-'}
                      </span>
                    </div>

                    {/* Row 8: Keterangan */}
                    <div className="px-5 py-4">
                      <label className="text-slate-600 dark:text-slate-400 font-medium text-sm block mb-1.5">
                        Keterangan
                      </label>
                      <div className="bg-white dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-xl p-3 min-h-[44px] text-slate-700 dark:text-slate-200 text-sm font-medium leading-relaxed">
                        {detailPembelian?.keterangan || '-'}
                      </div>
                    </div>

                    {/* Section Foto dengan Kaca Pembesar (Magnifying Glass Lens) */}
                    {imgSrc && (
                      <div className="mt-3 mb-2">
                        <ImageMagnifier
                          src={imgSrc}
                          alt={`Foto Pembelian ${detailPembelian?.kode_trx || ''}`}
                          zoomLevel={2.5}
                          lensSize={145}
                          maxHeight="280px"
                          badgeTitle={pembelianInfo.namaJenis !== '-' ? pembelianInfo.namaJenis : 'Perhiasan Emas'}
                          onPreviewClick={() => {
                            setPreviewImgSrc(imgSrc);
                            setOpenImage(true);
                          }}
                        />
                      </div>
                    )}

                  </div>
                    </>
                  ) : (
                    <>
                      {/* Item Terkait Tab */}
                      {hasItemTerkait ? (
                        <>
                          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700/80 mt-2 divide-y divide-slate-100 dark:divide-slate-700/50 overflow-hidden shadow-inner">
                            <div className="px-5 py-3 flex items-center justify-between text-sm">
                              <span className="text-slate-600 dark:text-slate-400 font-medium">Kode Item</span>
                              <span className="text-slate-700 dark:text-slate-200 font-bold">{itemTerkait?.kode_item || '-'}</span>
                            </div>
                            {itemTerkaitInfo.namaKategori && itemTerkaitInfo.namaKategori !== '-' && (
                              <div className="px-5 py-3 flex items-center justify-between text-sm">
                                <span className="text-slate-600 dark:text-slate-400 font-medium">Kategori</span>
                                <span className="text-slate-700 dark:text-slate-200 font-semibold">{itemTerkaitInfo.namaKategori}</span>
                              </div>
                            )}
                            <div className="px-5 py-3 flex items-center justify-between text-sm">
                              <span className="text-slate-600 dark:text-slate-400 font-medium">Nama Barang</span>
                              <span className="text-slate-700 dark:text-slate-200 font-bold">{itemTerkaitInfo.namaJenis}</span>
                            </div>
                            <div className="px-5 py-3 flex items-center justify-between text-sm">
                              <span className="text-slate-600 dark:text-slate-400 font-medium">Berat</span>
                              <span className="text-amber-600 dark:text-amber-400 font-bold">{roundBerat(itemTerkait?.berat)} gr</span>
                            </div>
                            <div className="px-5 py-3 flex items-center justify-between text-sm">
                              <span className="text-slate-600 dark:text-slate-400 font-medium">Kadar Emas</span>
                              <span className="text-amber-600 dark:text-amber-400 font-bold">{itemTerkait?.karat} krt</span>
                            </div>
                            <div className="px-5 py-3 flex items-center justify-between text-sm">
                              <span className="text-slate-600 dark:text-slate-400 font-medium">Harga Modal</span>
                              <span className="text-slate-700 dark:text-slate-200 font-bold">Rp {numberWithCommas(itemTerkait?.harga_modal)}</span>
                            </div>
                            <div className="px-5 py-3 flex items-center justify-between text-sm">
                              <span className="text-slate-600 dark:text-slate-400 font-medium">Created</span>
                              <span className="text-slate-700 dark:text-slate-200 font-bold">{itemTerkait?.created_at || '-'}</span>
                            </div>
                          </div>

                          {/* Section Foto Item Terkait di paling bawah data */}
                          {itemTerkaitImgSrc && (
                            <div className="mt-3 mb-2">
                              <ImageMagnifier
                                src={itemTerkaitImgSrc}
                                alt={`Foto Item Terkait ${itemTerkait?.kode_item || ''}`}
                                zoomLevel={2.5}
                                lensSize={145}
                                maxHeight="280px"
                                badgeTitle={itemTerkaitInfo.namaJenis !== '-' ? itemTerkaitInfo.namaJenis : 'Item Terkait'}
                                onPreviewClick={() => {
                                  setPreviewImgSrc(itemTerkaitImgSrc);
                                  setOpenImage(true);
                                }}
                              />
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="py-12 px-4 flex flex-col items-center justify-center text-center bg-slate-50/50 dark:bg-slate-800/30 rounded-xl border border-dashed border-slate-200 dark:border-slate-700/80 mt-2">
                          <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 dark:text-slate-500 mb-2.5">
                            <IconInbox size={26} stroke={1.5} />
                          </div>
                          <p className="text-slate-700 dark:text-slate-200 font-semibold text-sm">
                            Tidak Ada Data Item Terkait
                          </p>
                          <p className="text-slate-400 dark:text-slate-500 text-xs mt-1 max-w-xs">
                            Transaksi pembelian ini tidak terikat dengan data item pada database.
                          </p>
                        </div>
                      )}

                      {/* {imgSrc && (
                        <div className="mt-3 mb-2">
                          <ImageMagnifier
                            src={imgSrc}
                            alt={`Foto Pembelian ${detailPembelian?.kode_trx || ''}`}
                            zoomLevel={2.5}
                            lensSize={145}
                            maxHeight="280px"
                            badgeTitle={detailPembelian?.jenis_kategori?.nama_jenis || 'Perhiasan Emas'}
                            onPreviewClick={() => setOpenImage(true)}
                          />
                        </div>
                      )} */}
                    </>
                  )}

                  {/* Footer Actions */}
                  <div className="mt-5 flex items-center justify-end pt-1">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-6 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 active:scale-95 text-slate-700 dark:text-slate-200 font-semibold text-sm rounded-xl border border-slate-200 dark:border-slate-700 transition shadow-xs cursor-pointer"
                    >
                      Tutup
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
